import { Link } from "react-router-dom";
import { ArrowUpRight, Check, Quote, Sparkles, ArrowRight } from "lucide-react";

const FOUNDER_PHOTO =
  "https://customer-assets.emergentagent.com/job_a516a1a4-1ad3-4aee-b2b9-86e902fa5ee3/artifacts/s94hyxmh_Gemini_Generated_Image_cvm3hwcvm3hwcvm3.png";

const audienceTags = [
  "Black families",
  "Neurodiverse students",
  "Working mothers",
  "First-gen college",
  "Top-tier admits",
  "Zero student debt",
  "Honors scholars",
  "Future founders",
];

const Pill = ({ children }) => (
  <span className="inline-flex items-center gap-2 border-2 border-black/15 bg-white px-4 py-2 text-xs uppercase tracking-[0.18em] font-semibold text-black">
    {children}
  </span>
);

const Hero = () => {
  return (
    <section data-testid="home-hero" className="relative">
      <div className="grid md:grid-cols-2 min-h-[92vh] md:min-h-[88vh]">
        {/* LEFT, PROBLEM (DARK) */}
        <div className="relative bg-black text-[#FBF9F6] px-6 md:px-12 lg:px-20 py-20 md:py-28 flex flex-col justify-between overflow-hidden">
          <div className="absolute inset-0 opacity-[0.12] pointer-events-none"
            style={{
              backgroundImage:
                "radial-gradient(circle at 20% 30%, rgba(249,115,22,0.6), transparent 50%), radial-gradient(circle at 80% 80%, rgba(250,204,21,0.4), transparent 45%)",
            }}
          />
          <div className="relative">
            <p data-testid="hero-eyebrow-left" className="text-orange-400 text-xs uppercase tracking-[0.28em] font-bold mb-8">
              The honest truth, first.
            </p>
            <h1 data-testid="hero-headline-left" className="font-serif text-4xl sm:text-5xl md:text-5xl lg:text-7xl font-black leading-[1.02] tracking-tight">
              You did everything <em className="italic text-orange-400">right</em>.
              <br />
              So why does college feel like it's <span className="text-yellow-400">about to break you?</span>
            </h1>
            <p className="mt-8 text-lg md:text-xl text-[#FBF9F6]/80 leading-relaxed max-w-xl font-light">
              You're juggling deadlines, FAFSA jargon, scholarship portals at midnight, and a quiet fear: that your brilliant child might still graduate buried in debt, or settle for less than they deserve.
            </p>

            <ul className="mt-10 space-y-3 text-[#FBF9F6]/90">
              <li className="flex items-start gap-3"><span className="mt-1.5 inline-block w-2 h-2 bg-orange-500" /> Student loan debt that follows you for decades.</li>
              <li className="flex items-start gap-3"><span className="mt-1.5 inline-block w-2 h-2 bg-orange-500" /> Counselors who don't see your child's full story.</li>
              <li className="flex items-start gap-3"><span className="mt-1.5 inline-block w-2 h-2 bg-orange-500" /> A scholarship system designed to be confusing.</li>
            </ul>
          </div>

          <div className="relative mt-12 flex items-center gap-4">
            <Link
              to="/services"
              data-testid="hero-see-how-btn"
              className="inline-flex items-center gap-2 bg-orange-500 text-black px-7 py-4 text-sm uppercase tracking-[0.18em] font-bold border-2 border-orange-500 hover:bg-yellow-400 hover:border-yellow-400 transition-colors"
            >
              See how it works
              <ArrowUpRight className="w-4 h-4" strokeWidth={2.5} />
            </Link>
            <span className="text-[#FBF9F6]/55 text-sm italic font-serif">No pressure. Just clarity.</span>
          </div>
        </div>

        {/* RIGHT, SOLUTION (LIGHT) */}
        <div className="relative bg-[#FBF9F6] text-black px-6 md:px-12 lg:px-20 py-20 md:py-28 flex flex-col justify-between">
          <div>
            <div className="inline-flex items-center gap-2 mb-8 border border-black/15 bg-white px-3 py-2">
              <span className="w-2 h-2 bg-orange-500" />
              <span className="text-[11px] uppercase tracking-[0.22em] font-bold text-black">
                Scholar Mosaic <span className="text-black/40">·</span> <span className="text-orange-600">Your Unique College Blueprint</span>
              </span>
            </div>
            <p data-testid="hero-eyebrow-right" className="text-black/55 text-xs uppercase tracking-[0.28em] font-bold mb-6">
              And here's the part nobody told you.
            </p>
            <h2 data-testid="hero-headline-right" className="font-serif text-4xl sm:text-5xl md:text-5xl lg:text-6xl font-black leading-[1.04] tracking-tight">
              Your child can get into a top-tier college, and you can pay <span className="bg-yellow-400 px-2 italic">$0 for it.</span>
            </h2>
            <p className="mt-7 text-lg md:text-xl text-black/70 leading-relaxed max-w-xl font-light">
              Scholar Mosaic is the strategy room I wish my own family had. Curated college lists, scholarship matches built around <em>your</em> kid (not a template), and a CEO who's been in your shoes, and is bringing receipts.
            </p>

            <div className="mt-10 grid sm:grid-cols-2 gap-5">
              <div className="border-2 border-black/10 bg-white p-6 card-shadow-hover" data-testid="hero-card-match">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 bg-orange-500 flex items-center justify-center"><Sparkles className="w-4 h-4 text-black" strokeWidth={2.5}/></div>
                  <h3 className="font-serif text-xl font-bold">We match your student</h3>
                </div>
                <p className="text-sm text-black/65 leading-relaxed">
                  Curated college and scholarship lists built around your child's strengths, identity, and ambition, not the same five names on every guidance counselor's desk.
                </p>
              </div>
              <div className="border-2 border-black/10 bg-white p-6 card-shadow-hover" data-testid="hero-card-debt">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 bg-yellow-400 flex items-center justify-center"><Check className="w-4 h-4 text-black" strokeWidth={3}/></div>
                  <h3 className="font-serif text-xl font-bold">Tuition covered. Debt-free.</h3>
                </div>
                <p className="text-sm text-black/65 leading-relaxed">
                  Our families move from guessing and Googling to fully funded, with grants, merit aid, and academic scholarships stacked in their favor.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-12">
            <p className="text-xs uppercase tracking-[0.22em] font-bold text-black/50 mb-4">Built for:</p>
            <div className="flex flex-wrap gap-2.5" data-testid="hero-audience-tags">
              {["Black students", "Neurodiverse learners", "Working moms", "First-gen families", "Honors-track teens"].map((t) => (
                <span key={t} className="inline-flex items-center bg-orange-500/10 text-orange-700 border border-orange-500/30 px-3 py-1.5 text-xs font-semibold tracking-wide rounded-sm">
                  {t}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Marquee = () => (
  <section data-testid="audience-marquee" className="bg-yellow-400 border-y-2 border-black overflow-hidden">
    <div className="flex marquee-track whitespace-nowrap py-5">
      {[...audienceTags, ...audienceTags].map((tag, i) => (
        <span key={i} className="flex items-center gap-8 px-8 font-serif italic text-2xl md:text-3xl text-black">
          {tag}
          <span className="inline-block w-2 h-2 bg-black rotate-45" aria-hidden />
        </span>
      ))}
    </div>
  </section>
);

const Founder = () => (
  <section data-testid="founder-section" className="bg-[#FBF9F6] py-24 md:py-36">
    <div className="mx-auto max-w-7xl px-6 md:px-12 lg:px-16">
      <div className="grid md:grid-cols-12 gap-12 md:gap-16 items-center">
        <div className="md:col-span-5 order-2 md:order-1">
          <div className="relative">
            <div className="absolute -top-4 -left-4 w-full h-full bg-orange-500" aria-hidden />
            <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-yellow-400" aria-hidden />
            <img
              src={FOUNDER_PHOTO}
              alt="Genie Dawkins, Founder & CEO"
              className="relative w-full aspect-square object-cover grayscale-[20%] contrast-[1.05]"
              data-testid="founder-photo"
            />
          </div>
        </div>

        <div className="md:col-span-7 order-1 md:order-2">
          <p className="text-orange-500 text-xs uppercase tracking-[0.28em] font-bold mb-5">Meet your strategist</p>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05]">
            I'm Genie. I help <em className="italic">your</em> kid go to <span className="bg-black text-yellow-400 px-2">their</span> dream school, without borrowing yours.
          </h2>
          <div className="mt-8 space-y-5 text-lg text-black/75 leading-relaxed font-light max-w-2xl">
            <p>
              I built this practice because too many of our kids, brilliant, gifted, neurodivergent, deeply talented, get filtered out of conversations they should be leading. And too many of us, their parents, get sold a story that says debt is just the cost of a great future.
            </p>
            <p>
              It isn't. There is a whole ecosystem of grants, merit scholarships, and academic awards that go unclaimed every year because nobody hands working parents a map. I am that map.
            </p>
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-3">
            <Link
              to="/book"
              data-testid="founder-book-cta"
              className="inline-flex items-center gap-2 bg-black text-[#FBF9F6] px-7 py-4 text-sm uppercase tracking-[0.18em] font-bold border-2 border-black hover:bg-orange-500 hover:border-orange-500 transition-colors"
            >
              Book a consult
              <ArrowUpRight className="w-4 h-4" strokeWidth={2.5} />
            </Link>
            <Link
              to="/services"
              data-testid="founder-services-link"
              className="inline-flex items-center gap-2 text-black font-bold text-sm uppercase tracking-[0.18em] border-b-2 border-black pb-1 hover:text-orange-500 hover:border-orange-500 transition-colors"
            >
              View services
            </Link>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const Outcomes = () => {
  const stats = [
    { k: "100%", v: "Tuition covered for placed students", color: "bg-orange-500", text: "text-black" },
    { k: "$0", v: "In new student loans taken on", color: "bg-black", text: "text-yellow-400" },
    { k: "Top 50", v: "Average school admit tier", color: "bg-yellow-400", text: "text-black" },
    { k: "1:1", v: "Strategy, not a template", color: "bg-[#FBF9F6] border-2 border-black", text: "text-black" },
  ];
  return (
    <section data-testid="outcomes-section" className="bg-black py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-6 md:px-12 lg:px-16">
        <div className="grid md:grid-cols-12 gap-10 items-end mb-14">
          <div className="md:col-span-7">
            <p className="text-orange-400 text-xs uppercase tracking-[0.28em] font-bold mb-5">What this looks like in real life</p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-black text-[#FBF9F6] leading-[1.05] tracking-tight">
              Receipts <em className="italic text-yellow-400">over</em> hype.
            </h2>
          </div>
          <div className="md:col-span-5">
            <p className="text-[#FBF9F6]/75 text-lg font-light leading-relaxed">
              We don't just promise possibility. We track outcomes, acceptances, awards, dollars off the table, and the moment a mom realizes her kid actually <em>can</em> go anywhere.
            </p>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {stats.map((s, i) => (
            <div
              key={i}
              className={`${s.color} ${s.text} p-8 md:p-10 aspect-square flex flex-col justify-between`}
              data-testid={`outcome-stat-${i}`}
            >
              <span className="font-serif text-5xl md:text-6xl lg:text-7xl font-black tracking-tight leading-none">{s.k}</span>
              <span className="text-sm uppercase tracking-[0.18em] font-semibold leading-snug">{s.v}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const pathwayCards = [
  {
    id: "essentials",
    eyebrow: "Start here",
    name: "Essentials",
    price: "$99",
    cadence: "/ month",
    duration: "Introductory rate, goes to $149/month",
    bullets: [
      "Curated 15+ college list",
      "Monthly scholarship spreadsheets (6 months)",
      "Strategic narrative resume",
    ],
    style: "light",
    cta: "Start Essentials",
    testId: "home-pathway-essentials",
  },
  {
    id: "phase1",
    eyebrow: "Founding client rate",
    name: "Phase 1: College Planning Sprint",
    price: "$4,800",
    cadence: "/ 3, 4 months",
    duration: "College Planning Sprint, limited spots",
    bullets: [
      "College list, 10+ fellowships, benefits navigation",
      "College resume (2 versions) + scholarship tracker",
      "6 coaching calls, application planning overview",
    ],
    style: "dark",
    cta: "Apply for Phase 1",
    testId: "home-pathway-phase1",
  },
  {
    id: "full-journey",
    eyebrow: "Founding client rate",
    name: "Full Journey",
    price: "$8,500",
    cadence: "/ 8, 10 months",
    duration: "All 3 phases, complete college blueprint",
    bullets: [
      "All Phase 1 + Phase 2 + Phase 3 deliverables",
      "E.A.S.E. Self-Advocacy Document™ included",
      "13 total coaching calls + priority scheduling",
    ],
    style: "accent",
    cta: "Apply for Full Journey",
    testId: "home-pathway-full-journey",
  },
];

const PathwayCard = ({ c }) => {
  const isDark = c.style === "dark";
  const isAccent = c.style === "accent";

  const card = isDark
    ? "bg-black text-[#FBF9F6] border-black"
    : isAccent
    ? "bg-orange-500 text-black border-orange-500"
    : "bg-white text-black border-black/15";

  const eyebrow = isDark
    ? "text-orange-400"
    : isAccent
    ? "text-black"
    : "text-orange-500";

  const sub = isDark ? "text-[#FBF9F6]/70" : isAccent ? "text-black/80" : "text-black/60";
  const bulletTxt = isDark ? "text-[#FBF9F6]/90" : isAccent ? "text-black/90" : "text-black/80";
  const dotBg = isDark ? "bg-orange-500" : isAccent ? "bg-black" : "bg-black";
  const dotInk = isDark ? "text-black" : isAccent ? "text-yellow-400" : "text-yellow-400";

  const ctaCls = isDark
    ? "bg-orange-500 text-black border-orange-500 hover:bg-yellow-400 hover:border-yellow-400"
    : isAccent
    ? "bg-black text-[#FBF9F6] border-black hover:bg-yellow-400 hover:text-black hover:border-yellow-400"
    : "bg-black text-[#FBF9F6] border-black hover:bg-orange-500 hover:border-orange-500";

  return (
    <article
      data-testid={c.testId}
      className={`relative p-8 md:p-10 border-2 ${card} card-shadow-hover flex flex-col`}
    >
      <p className={`text-xs uppercase tracking-[0.28em] font-bold mb-5 ${eyebrow}`}>{c.eyebrow}</p>
      <h3 className="font-serif text-3xl md:text-4xl font-black tracking-tight leading-[1.05]">{c.name}</h3>

      <div className="mt-5 flex items-baseline gap-2">
        <span className="font-serif text-5xl md:text-6xl font-black tracking-tight leading-none">{c.price}</span>
        <span className={`text-base font-medium ${sub}`}>{c.cadence}</span>
      </div>
      <p className={`mt-2 text-xs uppercase tracking-[0.18em] font-bold ${eyebrow}`}>{c.duration}</p>

      <ul className="mt-7 space-y-3 flex-1">
        {c.bullets.map((b, i) => (
          <li key={i} className="flex items-start gap-3">
            <span className={`mt-0.5 inline-flex items-center justify-center w-5 h-5 flex-shrink-0 ${dotBg}`}>
              <Check className={`w-3 h-3 ${dotInk}`} strokeWidth={3} />
            </span>
            <span className={`text-sm leading-snug ${bulletTxt}`}>{b}</span>
          </li>
        ))}
      </ul>

      <Link
        to="/book"
        data-testid={`${c.testId}-cta`}
        className={`mt-9 inline-flex items-center justify-center gap-2 px-6 py-4 text-sm uppercase tracking-[0.18em] font-bold border-2 transition-colors ${ctaCls}`}
      >
        {c.cta}
        <ArrowUpRight className="w-4 h-4" strokeWidth={2.5} />
      </Link>
    </article>
  );
};

const Pathways = () => (
  <section data-testid="home-pathways" className="bg-[#FBF9F6] py-24 md:py-32 border-t border-black/10">
    <div className="mx-auto max-w-7xl px-6 md:px-12 lg:px-16">
      <div className="grid md:grid-cols-12 gap-10 items-end mb-14">
        <div className="md:col-span-7">
          <p className="text-orange-500 text-xs uppercase tracking-[0.28em] font-bold mb-5">Three pathways</p>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-black tracking-tight leading-[1.04]">
            Pick the room that fits <em className="italic">your</em> family right now.
          </h2>
        </div>
        <div className="md:col-span-5">
          <p className="text-lg text-black/70 font-light leading-relaxed">
            Three packages, one strategy desk. Start with a monthly map, step into a full partnership, or go all the way, with the E.A.S.E. Self-Advocacy Document™ built in.
          </p>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6 lg:gap-7">
        {pathwayCards.map((c) => (
          <PathwayCard key={c.id} c={c} />
        ))}
      </div>

      <div className="mt-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
        <p className="text-sm text-black/55 italic font-serif">
          Add-on: <span className="not-italic font-bold text-black">E.A.S.E. Self-Advocacy Document™, $1,200 flat</span> (included free in Full Journey).
        </p>
        <Link
          to="/services"
          data-testid="home-pathways-compare-link"
          className="inline-flex items-center gap-2 text-black font-bold text-sm uppercase tracking-[0.18em] border-b-2 border-black pb-1 hover:text-orange-500 hover:border-orange-500 transition-colors"
        >
          See the full services catalog
          <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
        </Link>
      </div>
    </div>
  </section>
);

const Testimonial = () => (
  <section data-testid="testimonial-section" className="bg-[#FBF9F6] py-24 md:py-36 relative overflow-hidden">
    <div className="absolute -top-10 left-10 w-40 h-40 bg-yellow-400/40 rounded-full blur-3xl" aria-hidden />
    <div className="absolute bottom-0 right-0 w-72 h-72 bg-orange-500/15 rounded-full blur-3xl" aria-hidden />

    <div className="mx-auto max-w-5xl px-6 md:px-12 lg:px-16 relative">
      <Quote className="w-14 h-14 text-orange-500 mb-8" strokeWidth={1.5} />
      <blockquote className="font-serif text-3xl md:text-5xl lg:text-6xl font-black text-black leading-[1.1] tracking-tight">
        "I was so surprised that <em className="italic">Genie</em> was able to find a college that <span className="bg-orange-500 px-2">fit my child.</span>"
      </blockquote>
      <div className="mt-10 flex items-center gap-4">
        <div className="w-12 h-12 bg-black flex items-center justify-center text-yellow-400 font-serif font-black text-lg">M.</div>
        <div>
          <p className="font-serif font-bold text-lg text-black">A relieved mom.</p>
          <p className="text-sm text-black/55 uppercase tracking-[0.18em]">Pathway program family</p>
        </div>
      </div>
    </div>
  </section>
);

const FinalCTA = () => (
  <section data-testid="final-cta-section" className="bg-orange-500 text-black py-24 md:py-32 relative grain">
    <div className="mx-auto max-w-7xl px-6 md:px-12 lg:px-16 relative">
      <div className="grid md:grid-cols-12 gap-10 items-end">
        <div className="md:col-span-8">
          <p className="text-xs uppercase tracking-[0.28em] font-bold mb-6">The next move is small. The next four years aren't.</p>
          <h2 className="font-serif text-4xl md:text-6xl lg:text-7xl font-black leading-[1.02] tracking-tight">
            Let's get your kid to <em className="italic">yes</em>, and your wallet to <span className="bg-black text-yellow-400 px-2">rest</span>.
          </h2>
        </div>
        <div className="md:col-span-4 flex md:justify-end">
          <Link
            to="/book"
            data-testid="final-cta-btn"
            className="inline-flex items-center gap-3 bg-black text-[#FBF9F6] px-8 py-5 text-sm uppercase tracking-[0.18em] font-bold border-2 border-black hover:bg-[#FBF9F6] hover:text-black transition-colors"
          >
            Book a consult
            <ArrowUpRight className="w-5 h-5" strokeWidth={2.5} />
          </Link>
        </div>
      </div>
    </div>
  </section>
);

const Home = () => {
  return (
    <div data-testid="page-home" className="-mt-32 md:-mt-44 pt-32 md:pt-44">
      <Hero />
      <Marquee />
      <Founder />
      <Pathways />
      <Outcomes />
      <Testimonial />
      <FinalCTA />
    </div>
  );
};

export default Home;
